/**
 * OpenPGP Key Status Detection Utility
 *
 * Provides functions to detect and analyze the status of OpenPGP keys,
 * including checking for existing keys and their attributes.
 */

import * as forge from 'node-forge';

/**
 * OpenPGP key type definitions
 */
export const OPENPGP_KEY_TYPES = [
  { id: 'sig', slot: '01', name: 'Signature Key', description: 'Used for signing data and emails' },
  { id: 'dec', slot: '02', name: 'Decryption Key', description: 'Used for decrypting data and emails' },
  { id: 'aut', slot: '03', name: 'Authentication Key', description: 'Used for SSH and authentication' }
];

/**
 * Check the status of an OpenPGP key
 * @param {string} keyType - Key type ('sig', 'dec', 'aut')
 * @param {Function} transceive - Function to send APDU commands
 * @returns {Promise<Object>} Key status information
 */
export async function checkOpenPGPKeyStatus(keyType, transceive) {
  const keyTypeObj = OPENPGP_KEY_TYPES.find(k => k.id === keyType);

  if (!keyTypeObj) {
    throw new Error(`Invalid OpenPGP key type: ${keyType}`);
  }

  const keySlot = keyTypeObj.slot;

  try {
    // Select OpenPGP applet first
    const selectAPDU = '00A4040006D27600012401';
    const selectResponse = await transceive(selectAPDU);

    if (!selectResponse.endsWith('9000')) {
      throw new Error('Failed to select OpenPGP applet');
    }

    // Try to read public key using GENERATE ASYMMETRIC KEY PAIR with reading mode
    // Command: 00 47 81 [slot] 00
    const readPubKeyAPDU = `004781${keySlot}00`;
    const pubKeyResponse = await transceive(readPubKeyAPDU);

    const hasKey = pubKeyResponse.endsWith('9000') && pubKeyResponse.length > 8;

    let keyInfo = null;
    if (hasKey) {
      try {
        keyInfo = await parseOpenPGPPublicKey(pubKeyResponse, keyType);
      } catch (error) {
        console.error(`Failed to parse OpenPGP key ${keyType}:`, error);
        keyInfo = { error: error.message };
      }
    }

    // Try to read key attributes to get algorithm info
    let keyAttributes = null;
    try {
      keyAttributes = await readKeyAttributes(keySlot, transceive);
    } catch (error) {
      console.error(`Failed to read key attributes for ${keyType}:`, error);
    }

    return {
      keyType,
      status: hasKey ? 'configured' : 'empty',
      hasKey,
      keyInfo,
      keyAttributes,
      lastChecked: new Date().toISOString()
    };

  } catch (error) {
    console.error(`Failed to check OpenPGP key ${keyType}:`, error);
    return {
      keyType,
      status: 'error',
      error: error.message,
      lastChecked: new Date().toISOString()
    };
  }
}

/**
 * Read key attributes from OpenPGP card
 * @param {string} keySlot - Key slot ('01', '02', '03')
 * @param {Function} transceive - Function to send APDU commands
 * @returns {Promise<Object>} Key attributes
 */
async function readKeyAttributes(keySlot, transceive) {
  // Data Object tags for key attributes
  const attributeTags = {
    '01': 'C1', // Signature key attributes
    '02': 'C2', // Decryption key attributes
    '03': 'C3'  // Authentication key attributes
  };

  const tag = attributeTags[keySlot];
  if (!tag) {
    throw new Error(`Invalid key slot: ${keySlot}`);
  }

  // Read key attributes: 00 CA 00 [tag] 00
  const readAttrAPDU = `00CA00${tag}00`;
  const response = await transceive(readAttrAPDU);

  if (!response.endsWith('9000')) {
    throw new Error('Failed to read key attributes');
  }

  // Parse attributes
  const attrData = response.slice(0, -4);
  return parseKeyAttributes(attrData);
}

/**
 * Parse OpenPGP key attributes
 * @param {string} attrHex - Attribute data in hex
 * @returns {Object} Parsed attributes
 */
function parseKeyAttributes(attrHex) {
  try {
    if (attrHex.length < 2) {
      return { algorithm: 'Unknown' };
    }

    const algorithmId = attrHex.substr(0, 2);

    // Algorithm IDs according to OpenPGP specification
    const algorithms = {
      '01': 'RSA',
      '12': 'ECDH',
      '13': 'ECDSA',
      '16': 'EdDSA'
    };

    const algorithm = algorithms[algorithmId] || 'Unknown';

    let keySize = 'Unknown';

    if (algorithmId === '01') {
      // RSA: next 2 bytes are key size in bits (big-endian)
      if (attrHex.length >= 6) {
        keySize = parseInt(attrHex.substr(2, 4), 16);
      }
    } else if (['12', '13', '16'].includes(algorithmId)) {
      // ECC: parse curve OID
      const curveInfo = parseECCCurve(attrHex.substr(2));
      keySize = curveInfo;
    }

    return {
      algorithm,
      keySize
    };

  } catch (error) {
    console.error('Failed to parse key attributes:', error);
    return { algorithm: 'Unknown', error: error.message };
  }
}

/**
 * Parse ECC curve information
 * @param {string} curveData - Curve data in hex
 * @returns {string} Curve name
 */
function parseECCCurve(curveData) {
  // Common curve OIDs
  const curves = {
    '2A8648CE3D030107': 'P-256',
    '2B81040022': 'P-384',
    '2B81040023': 'P-521',
    '2B060104019755010501': 'Curve25519'
  };

  for (const [oid, name] of Object.entries(curves)) {
    if (curveData.includes(oid)) {
      return name;
    }
  }

  return 'Unknown ECC';
}

/**
 * Parse OpenPGP public key information
 * @param {string} pubKeyResponse - APDU response containing public key
 * @param {string} keyType - Key type
 * @returns {Object} Parsed key information
 */
async function parseOpenPGPPublicKey(pubKeyResponse, keyType) {
  try {
    const keyData = pubKeyResponse.slice(0, -4);

    // Parse TLV structure
    const keyComponents = parsePublicKeyTLV(keyData);

    // Determine algorithm and key size
    let algorithm = 'Unknown';
    let keySize = 'Unknown';

    if (keyComponents.modulus) {
      // RSA key
      algorithm = 'RSA';
      keySize = keyComponents.modulus.length * 4; // hex chars to bits
    } else if (keyComponents.publicPoint) {
      // ECC key
      algorithm = 'ECC';
      keySize = keyComponents.curve || 'Unknown';
    }

    // Calculate fingerprint
    let fingerprint = 'Unknown';
    try {
      const hash = forge.md.sha256.create();
      hash.update(keyData);
      fingerprint = hash.digest().toHex().substring(0, 16).toUpperCase();
    } catch (error) {
      console.error('Failed to calculate fingerprint:', error);
    }

    return {
      keyType,
      algorithm,
      keySize,
      fingerprint,
      createdAt: new Date().toISOString()
    };

  } catch (error) {
    console.error('Failed to parse OpenPGP public key:', error);
    throw new Error(`Public key parsing failed: ${error.message}`);
  }
}

/**
 * Parse public key TLV structure
 * @param {string} tlvHex - TLV data in hex
 * @returns {Object} Key components
 */
function parsePublicKeyTLV(tlvHex) {
  const components = {};

  try {
    let pos = 0;

    while (pos < tlvHex.length - 4) {
      const tag = tlvHex.substr(pos, 2);
      pos += 2;

      if (pos >= tlvHex.length) break;

      // Parse length
      let length;
      const firstLengthByte = parseInt(tlvHex.substr(pos, 2), 16);

      if (firstLengthByte < 0x80) {
        length = firstLengthByte;
        pos += 2;
      } else if (firstLengthByte === 0x81) {
        length = parseInt(tlvHex.substr(pos + 2, 2), 16);
        pos += 4;
      } else if (firstLengthByte === 0x82) {
        length = parseInt(tlvHex.substr(pos + 2, 4), 16);
        pos += 6;
      } else {
        break;
      }

      const value = tlvHex.substr(pos, length * 2);

      // Tag 81 = modulus (RSA)
      // Tag 82 = exponent (RSA)
      // Tag 86 = public point (ECC)
      if (tag === '81') {
        components.modulus = value;
      } else if (tag === '82') {
        components.exponent = value;
      } else if (tag === '86') {
        components.publicPoint = value;
      }

      pos += length * 2;
    }
  } catch (error) {
    console.error('TLV parsing error:', error);
  }

  return components;
}

/**
 * Check all OpenPGP keys in parallel
 * @param {Function} transceive - Function to send APDU commands
 * @returns {Promise<Array>} Array of key status objects
 */
export async function checkAllOpenPGPKeys(transceive) {
  const keyChecks = OPENPGP_KEY_TYPES.map(keyType =>
    checkOpenPGPKeyStatus(keyType.id, transceive)
  );

  return await Promise.all(keyChecks);
}

/**
 * Get statistics about OpenPGP key statuses
 * @param {Array} keyStatuses - Array of key status objects
 * @returns {Object} Statistics summary
 */
export function getOpenPGPKeyStatistics(keyStatuses) {
  return {
    total: keyStatuses.length,
    empty: keyStatuses.filter(k => k.status === 'empty').length,
    configured: keyStatuses.filter(k => k.status === 'configured').length,
    errors: keyStatuses.filter(k => k.status === 'error').length
  };
}

/**
 * Get human-readable status label
 * @param {string} status - Status code
 * @returns {string} Human-readable label
 */
export function getStatusLabel(status) {
  const labels = {
    'empty': 'Empty',
    'configured': 'Configured',
    'error': 'Error'
  };

  return labels[status] || 'Unknown';
}

/**
 * Get recommended action based on key status
 * @param {string} status - Key status
 * @returns {string} Recommended action
 */
export function getRecommendedAction(status) {
  const recommendations = {
    'empty': 'generate',
    'configured': 'skip',
    'error': 'skip'
  };

  return recommendations[status] || 'skip';
}
